/* @jsxImportSource @emotion/react */

import React from 'react'
import { css } from '@emotion/react'
import classNames from 'classnames'
import type { IntlShape } from 'react-intl'
import { FormattedMessage } from 'react-intl'

import { ImgSanity } from '@slite/components'

import { Button } from '../../components/Button/Button'
import { ConnectedSourcesIllustration } from '../../components/illustrations/ConnectedSourcesIllustration'
import { OutlinedButtonIllustration } from '../../components/illustrations/OutlinedButtonIllustration'
import type { useRoutes } from '../../routes'

const SMALL_BREAKPOINT = 640

export function getBrowserExtensionWorkflowsSteps(intl: IntlShape) {
  return [
    {
      title: intl.formatMessage({
        id: 'browserExtension.steps.1.title',
        defaultMessage: 'Open any webpage in your browser',
      }),
      desc: intl.formatMessage({
        id: 'browserExtension.steps.1.desc',
        defaultMessage:
          'An internal platform, your help center, a marketing site — you name it.',
      }),
      illustration: (
        <div className="padding-top-32 padding-top-0-small padding-right-24-small">
          <ImgSanity
            className="block"
            src="https://cdn.sanity.io/images/dnsgqii5/production/fca11fa8c3efdfac677b76d6ea030c29b44835f8-1176x698.png"
            alt=""
          />
        </div>
      ),
    },
    {
      title: intl.formatMessage({
        id: 'browserExtension.steps.2.title',
        defaultMessage: 'Open the Slite browser extension',
      }),
      desc: intl.formatMessage({
        id: 'browserExtension.steps.2.desc',
        defaultMessage:
          'Click the Slite extension button or use the keyboard shortcut.',
      }),
      illustration: (
        <div className="flexbox-grow flexbox flexbox-align-end padding-top-32 padding-top-0-small padding-right-40">
          <ImgSanity
            className="block"
            src="https://cdn.sanity.io/images/dnsgqii5/production/037e135fa189aebbcdc48c92c91290d34bc74c72-1176x698.png"
            alt=""
          />
        </div>
      ),
    },
    {
      title: intl.formatMessage({
        id: 'browserExtension.steps.3.title',
        defaultMessage:
          'Ask anything — Slite answers with context from your page',
      }),
      desc: intl.formatMessage({
        id: 'browserExtension.steps.3.desc',
        defaultMessage: 'And combines it with your connected knowledge.',
      }),
      illustration: (
        <div
          className="padding-top-0-small padding-right-24-small padding-bottom-32 padding-bottom-0-small flexbox flexbox-justify-end flexbox-no-shrink"
          css={css`
            @media screen and (min-width: ${SMALL_BREAKPOINT}px) {
              max-width: 55%;
            }
          `}
        >
          <ImgSanity
            className="block"
            src="https://cdn.sanity.io/images/dnsgqii5/production/cabdee3427282b78b45f73bf1e2af495b880d14b-1156x740.png"
            alt=""
          />
        </div>
      ),
    },
  ]
}

export function getBrowserExtensionButtonsSteps(
  intl: IntlShape,
  routes: ReturnType<typeof useRoutes>
) {
  return [
    {
      title: intl.formatMessage({
        id: 'browserExtension.buttonSteps.1.title',
        defaultMessage: 'Define where your button lives',
      }),
      desc: intl.formatMessage({
        id: 'browserExtension.buttonSteps.1.desc',
        defaultMessage:
          'Use the default buttons like Intercom or Zendesk, or create your own on any web tool.',
      }),
      illustration: (
        <div className="flexbox-grow flexbox flexbox-centered shade-10">
          <OutlinedButtonIllustration width={200} height={200} />
        </div>
      ),
    },
    {
      title: intl.formatMessage({
        id: 'browserExtension.buttonSteps.2.title',
        defaultMessage: 'Set the sources and instructions',
      }),
      desc: intl.formatMessage({
        id: 'browserExtension.buttonSteps.2.desc',
        defaultMessage:
          'Choose what knowledge Slite should pull from to answer when you use this button.',
      }),
      illustration: (
        <div className="flexbox-grow flexbox flexbox-centered shade-20">
          <ConnectedSourcesIllustration />
        </div>
      ),
    },
    {
      title: intl.formatMessage({
        id: 'browserExtension.buttonSteps.3.title',
        defaultMessage: 'Install the extension',
      }),
      desc: intl.formatMessage({
        id: 'browserExtension.buttonSteps.3.desc',
        defaultMessage:
          'Go to the Chrome Web Store and install the Slite browser extension.',
      }),
      illustration: (
        <div className="width-full padding-horizontal-40 margin-auto flexbox-grow flexbox flexbox-column padding-bottom-40">
          <ImgSanity
            style={{ maxWidth: 140 }}
            className="block margin-auto margin-bottom-40"
            src="https://cdn.sanity.io/images/dnsgqii5/production/1140bc5848789ddee0e4536890fd637c99ab34c0-368x368.png"
            alt=""
          />
          <Button
            variant="primary"
            className="width-full"
            size="huge"
            href={routes.external.agentChromeExtension.url}
          >
            <FormattedMessage
              id="browserExtension.buttonSteps.3.buttonLabel"
              defaultMessage="Add to Chrome"
            />
          </Button>
        </div>
      ),
    },
    {
      title: intl.formatMessage({
        id: 'browserExtension.buttonSteps.4.title',
        defaultMessage: 'Save hours with one-click workflows',
      }),
      desc: intl.formatMessage({
        id: 'browserExtension.buttonSteps.4.desc',
        defaultMessage:
          'Contextual buttons handle your most repetitive tasks, instantly.',
      }),
      illustration: (
        <div
          className={classNames(
            'flexbox-no-shrink ',
            'flexbox-grow flexbox flexbox-align-start '
          )}
          css={css`
            @media screen and (min-width: ${SMALL_BREAKPOINT}px) {
              max-width: 55%;
            }
          `}
        >
          <ImgSanity
            className="block"
            src="https://cdn.sanity.io/images/dnsgqii5/production/27ba6ec801328fb5ed3688cffbe6f40c74edff93-1440x740.png"
            alt=""
          />
        </div>
      ),
    },
  ]
}
